import {FilteringProperty} from "@amzn/awsui-components-react/polaris/property-filter/interfaces";
import {
    compareNullableNumbers,
    NULLABLE_NUMBER_OPERATORS,
    columnDefinitionToOption,
    configPropsToColumnDefinitions,
    configPropsToFilterProps,
    dateComparison,
    formatDecimal,
    NUMBER_FIELD_OPERATORS,
    TableContentConfigProps,
    TEXT_FIELD_OPERATORS
} from "../tableConfig";
import {formatRatio, verdictDisplay} from "./mapPackValidationSummaryFormat";
import {Badge, Box, CollectionPreferencesProps} from "@amzn/awsui-components-react";
import EpochDateField from "../../components/offline-maps/EpochDateField";
import {formatDate, TableDateFilterForm} from "../../components/offline-maps/TableDateFilterForm";
import {comparePnfNumbers, PNF_NUMBER_OPERATORS, RecostPnfTotals} from "./recostPnfMetrics";
import RecostPnfCount from "../../components/mapRelease/RecostPnfCount";
import RecostMapIdentity from "../../components/mapRelease/RecostMapIdentity";

import { MapRecostLatency, RecostLatencyChanges } from "./recostLatencyMetrics";
import RecostLatencyChange from "../../components/mapRelease/RecostLatencyChange";

export interface MapPackValidationData extends RecostPnfTotals, MapRecostLatency, RecostLatencyChanges {
    mapPack: string;
    mapPackControl: string;
    mwoWorkflowType: string;
    recostingDataSource: string;
    trecsRecostingLink: string;
    region: string;
    summaryId: string;
    rsmVersion: string;
    vehicleType: string;
    mwoBasemapRefresh: boolean;
    /** Null when the control map pack had no on-zone path-not-found failures. See formatRatio. */
    pnfPercentIncrease: number | null;
    dpmo: number;
    /** Null when the control map pack recorded no failed trips. See formatRatio. */
    dpmoChange: number | null;
    dpmoControl: number;
    failedTripCount: number;
    failedTripCountControl: number;
    summaryStationCount: number;
    recostingStationCount: number;
    stationCount: number;
    /** Null on builds that predate this counter, which is not the same as zero. See formatCount. */
    onZonePnfFailureCountControl: number | null;
    /** Null on builds that predate this counter, which is not the same as zero. See formatCount. */
    onZonePnfFailureCountExperiment: number | null;
    /**
     * What the MWO recost gate concluded about this map pack. Null when the gate never recorded one, which
     * is not the same as it having declined to judge the pack. See verdictDisplay.
     */
    recostGateVerdict: string | null;
    /** The sentence the gate recorded alongside the verdict. Null whenever the verdict is. */
    recostGateReason: string | null;
    stemPnfFailureCount: number | null;
    stemPnfFailureCountControl: number | null;
    increasedTotalPnfStationCount: number | null;
    stemTransitTimeBreachingStationCount: number;
    transitTimeBreachingStationCount: number;
    tripCount: number;
    stemSnapFailureCount: number;
    stemSnapFailureCountControl: number;
    createdAtEpochSecs: number;
    CreatedAtEpochSecsSimulations: number;
    totalSimulationStationIds:number;
    trecsSimulationLink: string;
    dpphBreachCount:number;
    sprBreachCount:number;
    averageCurrentSpr: number;
    averageDpphDelta: number;
    averageOriginalSpr: number;
    averageSprDelta:number;
    simulationSummaryId:number;
    averageCurrentDpph:string;
    averageOriginalDpph: string;
}

// Simulation columns are omitted at BI/SME's request: simulations have not been working,
// so these columns currently provide no useful information during map-release review.
// Keep simulation fields in MapPackValidationData and the store for CSV exports.
// When needed, restore their entries here from version history to restore the columns,
// column-picker options, and property filters generated from this configuration.
const MAP_PACK_VALIDATION_SUMMARY_TABLE_CONTENT_CONFIG: TableContentConfigProps<MapPackValidationData>[] =
    [
        {
            id: "mapPack",
            description: "Candidate Map",
            width: 280,
            minWidth: 220,
            maxWidth: 280,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <RecostMapIdentity candidate={mapPackValidationData.mapPack}
                    control={mapPackValidationData.mapPackControl}
                    vehicleType={mapPackValidationData.vehicleType}
                    recostingDataSource={mapPackValidationData.recostingDataSource}/>
            ),
            sortingField: "mapPack",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            // Second column deliberately: the point is that a reviewer sees a pack will not be released
            // when they first read the recost report, not several steps later when the release button
            // refuses. Sorted and filtered on the stored value rather than the label, so a saved filter
            // survives a wording change here.
            id: "recostGateVerdict",
            description: "Recost Gate",
            width: 190,
            minWidth: 120,
            maxWidth: 190,
            tableCell: (mapPackValidationData: MapPackValidationData) => {
                const display = verdictDisplay(mapPackValidationData.recostGateVerdict);
                return (
                    <Box>
                        <Badge color={display.color}>{display.label}</Badge>
                        {display.showReason && mapPackValidationData.recostGateReason && (
                            <Box variant="small" padding={{top: "xxs"}}>
                                {mapPackValidationData.recostGateReason}
                            </Box>
                        )}
                    </Box>
                );
            },
            sortingField: "recostGateVerdict",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "tripCount",
            description: "Trips",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.tripCount}</Box>
            ),
            sortingField: "tripCount",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "latencyP50ChangePercent",
            description: "P50 Latency Change (%)",
            width: 200,
            minWidth: 170,
            maxWidth: 220,
            tableCell: (row: MapPackValidationData) => (
                <RecostLatencyChange change={row.latencyP50ChangePercent}
                    control={row.clientLatencyP50ControlMs} candidate={row.clientLatencyP50CandidateMs}
                    pairedCount={row.clientLatencyPairedCount} recordCount={row.clientLatencyRecordCount}/>
            ),
            sortingComparator: (lhs, rhs) => compareNullableNumbers(lhs.latencyP50ChangePercent, rhs.latencyP50ChangePercent),
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NULLABLE_NUMBER_OPERATORS,
        },
        {
            id: "latencyP99ChangePercent",
            description: "P99 Latency Change (%)",
            width: 200,
            minWidth: 170,
            maxWidth: 220,
            tableCell: (row: MapPackValidationData) => (
                <RecostLatencyChange change={row.latencyP99ChangePercent}
                    control={row.clientLatencyP99ControlMs} candidate={row.clientLatencyP99CandidateMs}
                    pairedCount={row.clientLatencyPairedCount} recordCount={row.clientLatencyRecordCount}/>
            ),
            sortingComparator: (lhs, rhs) => compareNullableNumbers(lhs.latencyP99ChangePercent, rhs.latencyP99ChangePercent),
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NULLABLE_NUMBER_OPERATORS,
        },
        {
            id: "totalPnfCountControl",
            description: "Total PNF Control",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <RecostPnfCount value={mapPackValidationData.totalPnfCountControl}/>
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.totalPnfCountControl, rhs.totalPnfCountControl),
            filterGroup: "mapPackValidationTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "totalPnfCountCandidate",
            description: "Total PNF Candidate",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <RecostPnfCount value={mapPackValidationData.totalPnfCountCandidate} control={mapPackValidationData.totalPnfCountControl}/>
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.totalPnfCountCandidate, rhs.totalPnfCountCandidate),
            filterGroup: "mapPackValidationTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "pnfNetChange",
            description: "PNF Net Change",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <RecostPnfCount value={mapPackValidationData.pnfNetChange} signed/>
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.pnfNetChange, rhs.pnfNetChange),
            filterGroup: "mapPackValidationTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "increasedTotalPnfStationCount",
            description: "Stations with Increased Total PNF",
            width: 190,
            minWidth: 120,
            maxWidth: 190,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <RecostPnfCount value={mapPackValidationData.increasedTotalPnfStationCount} control={0}/>
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.increasedTotalPnfStationCount, rhs.increasedTotalPnfStationCount),
            filterGroup: "mapPackValidationTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "recostingResult",
            description: "Station Summary",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <a href={"/station-recosting-summary?id=" + mapPackValidationData.summaryId}>Link</a>
            ),
            sortingField: "recostingResult",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "onZonePnfFailureCountControl",
            description: "On-zone PNF Control",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <RecostPnfCount value={mapPackValidationData.onZonePnfFailureCountControl}/>
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.onZonePnfFailureCountControl, rhs.onZonePnfFailureCountControl),
            filterGroup: "mapPackValidationTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "onZonePnfFailureCountExperiment",
            description: "On-zone PNF Candidate",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <RecostPnfCount value={mapPackValidationData.onZonePnfFailureCountExperiment} control={mapPackValidationData.onZonePnfFailureCountControl}/>
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.onZonePnfFailureCountExperiment, rhs.onZonePnfFailureCountExperiment),
            filterGroup: "mapPackValidationTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "stemPnfFailureCountControl",
            description: "Stem PNF Control",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <RecostPnfCount value={mapPackValidationData.stemPnfFailureCountControl}/>
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.stemPnfFailureCountControl, rhs.stemPnfFailureCountControl),
            filterGroup: "mapPackValidationTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "stemPnfFailureCount",
            description: "Stem PNF Candidate",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <RecostPnfCount value={mapPackValidationData.stemPnfFailureCount} control={mapPackValidationData.stemPnfFailureCountControl}/>
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.stemPnfFailureCount, rhs.stemPnfFailureCount),
            filterGroup: "mapPackValidationTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "transitTimeBreachingStationCount",
            description: "Overall TT Breaches (4%)",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.transitTimeBreachingStationCount}</Box>
            ),
            sortingField: "transitTimeBreachingStationCount",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stemTransitTimeBreachingStationCount",
            description: "Stem TT Breaches (10%)",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.stemTransitTimeBreachingStationCount}</Box>
            ),
            sortingField: "stemTransitTimeBreachingStationCount",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "createdAtEpochSecs",
            description: "Created",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <EpochDateField epochTime={mapPackValidationData.createdAtEpochSecs * 1000}/>
            ),
            sortingField: "createdAtEpochSecs",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS.map((operator) => ({
                operator,
                form: TableDateFilterForm,
                format: formatDate,
                match: (itemValue: any, tokenValue: any) =>
                    dateComparison(itemValue * 1000, tokenValue, operator),
            })),
        },
        {
            id: "mapPackControl",
            description: "Control Map",
            width: 280,
            minWidth: 220,
            maxWidth: 280,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <span style={{overflowWrap: "anywhere"}}>{mapPackValidationData.mapPackControl}</span>
            ),
            sortingField: "mapPackControl",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "rsmVersion",
            description: "RSM Version",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.rsmVersion}</Box>
            ),
            sortingField: "rsmVersion",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "vehicleType",
            description: "Vehicle",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.vehicleType}</Box>
            ),
            sortingField: "vehicleType",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "stationCount",
            description: "Stations Included / Recosted",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.stationCount}/{mapPackValidationData.recostingStationCount}</Box>
            ),
            sortingField: "stationCount",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "pnfPercentIncrease",
            description: "On-zone PNF % Change",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{formatRatio(mapPackValidationData.pnfPercentIncrease,
                    mapPackValidationData.onZonePnfFailureCountControl)}</Box>
            ),
            sortingField: "pnfPercentIncrease",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "failedTripCountControl",
            description: "All Failures Control",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.failedTripCountControl}</Box>
            ),
            sortingField: "failedTripCountControl",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "failedTripCount",
            description: "All Failures Candidate",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.failedTripCount}</Box>
            ),
            sortingField: "failedTripCount",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "dpmoControl",
            description: "Failure DPMO Control",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{formatDecimal(mapPackValidationData.dpmoControl)}</Box>
            ),
            sortingField: "dpmoControl",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "dpmo",
            description: "Failure DPMO Candidate",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{formatDecimal(mapPackValidationData.dpmo)}</Box>
            ),
            sortingField: "dpmo",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "dpmoChange",
            description: "Failure DPMO Change",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{formatRatio(mapPackValidationData.dpmoChange, mapPackValidationData.dpmoControl)}</Box>
            ),
            sortingField: "dpmoChange",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stemSnapFailureCountControl",
            description: "Stem Snap Control",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.stemSnapFailureCountControl}</Box>
            ),
            sortingField: "stemSnapFailureCountControl",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stemSnapFailureCount",
            description: "Stem Snap Candidate",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.stemSnapFailureCount}</Box>
            ),
            sortingField: "stemSnapFailureCount",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "mwoWorkflowType",
            description: "Workflow",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.mwoWorkflowType}</Box>
            ),
            sortingField: "mwoWorkflowType",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "recostingDataSource",
            description: "Recost Source",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <Box>{mapPackValidationData.recostingDataSource}</Box>
            ),
            sortingField: "recostingDataSource",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "trecsRecostingLink",
            description: "TRecS Report",
            width: 140,
            minWidth: 120,
            maxWidth: 140,
            tableCell: (mapPackValidationData: MapPackValidationData) => (
                <a href={mapPackValidationData.trecsRecostingLink}>Link</a>
            ),
            sortingField: "trecsRecostingLink",
            filterGroup: "mapPackValidationTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
    ];

export const MAP_PACK_VALIDATION_SUMMARY_TABLE_COLUMN_DEFINITIONS =
    configPropsToColumnDefinitions(MAP_PACK_VALIDATION_SUMMARY_TABLE_CONTENT_CONFIG);

// Secondary context stays available in the column picker and full CSV download.
export const MAP_PACK_VALIDATION_SUMMARY_DEFAULT_VISIBLE_COLUMNS =
    MAP_PACK_VALIDATION_SUMMARY_TABLE_COLUMN_DEFINITIONS.map((column) => column.id!)
        .filter((id) => !["mwoWorkflowType", "recostingDataSource", "trecsRecostingLink"].includes(id));

export const MAP_PACK_VALIDATION_SUMMARY_TABLE_VISIBLE_CONTENT_OPTIONS: any = [
    {
        label: "Map Release Dashboard",
        options: columnDefinitionToOption(MAP_PACK_VALIDATION_SUMMARY_TABLE_COLUMN_DEFINITIONS),
    },
];

export const MAP_PACK_VALIDATION_SUMMARY_TABLE_FILTER_PROPERTIES: FilteringProperty[] =
    configPropsToFilterProps(MAP_PACK_VALIDATION_SUMMARY_TABLE_CONTENT_CONFIG).sort((a, b) =>
        a.propertyLabel.localeCompare(b.propertyLabel)
    );

export const PAGE_SIZE_OPTIONS: CollectionPreferencesProps.PageSizeOption[] = [
    {value: 20, label: "20 Entries"},
    {value: 50, label: "50 Entries"},
    {value: 100, label: "100 Entries"},
];